<template>
  <div class="mis-comisiones-resultado mt-2">
    <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mis-comisiones-resultados-header">
      <div class="text-muted">
        {{ totalItems }} {{ $t("mis_datos.comisiones.resultados.total") }}
      </div>
      <div class="d-flex align-items-center gap-2 mis-comisiones-resultados-acciones">
        <DxButton
          v-bind="{ ...defaultButtonProps(undefined, true), icon: 'pdffile' }"
          text="Descargar PDF"
          :disabled="!descargaCondicionesHabilitada"
          @click="descargarCondicionesComisionPDF"
        />
        <DxButton
          v-bind="{ ...defaultButtonProps(undefined, true), icon: 'xlsfile' }"
          text="Descargar Excel"
          :disabled="!descargaCondicionesHabilitada"
          @click="descargarCondicionesComisionExcel"
        />
      </div>
    </div>
    <DxScrollView ref="scrollViewRef" :use-native="true" :scroll-by-content="true" :scroll-by-thumb="false" show-scrollbar="always"
      :bounce-enabled="false" :reach-bottom-enabled="reachBottomEnabled" reach-bottom-text="" @reach-bottom="onReachBottom">
      <div class="d-flex flex-column gap-3 pb-3 px-2">
        <MisComisionesResultadoCard v-for="(item, index) in items" :key="index" :model="item" :impuestos-activo="impuestosActivo"/>
        <p v-if="items.length === 0 && !loading && filtrosAplicados?.Comercializadora != null" class="text-muted mb-0">
          {{ $t("mis_datos.comisiones.resultados.vacio") }}
        </p>
        <p v-if="loading && items.length === 0 && hasMore" class="text-muted mb-0">
          {{ $t("mis_datos.comisiones.resultados.cargandoMas") }}
        </p>
        <p v-if="loading && items.length > 0 && hasMore" class="text-muted text-center mb-0 small">
          {{ $t("mis_datos.comisiones.resultados.cargandoMas") }}
        </p>
        <p v-if="loadError" class="text-danger mb-0">
          {{ $t("mis_datos.comisiones.resultados.error") }}
        </p>
      </div>
    </DxScrollView>
  </div>
</template>

<script setup lang="ts">
import DxScrollView from "devextreme-vue/scroll-view";
import { DxButton } from "devextreme-vue/button";
import type { OfertasEnergiaRetribucionesListFiltrosModel } from "@api/api";
import { defaultButtonProps } from "@/.shared/helpers/button-helper";
import { toastError, toastSuccess } from "@/.shared/services/notification-service";
import MisComisionesResultadoCard from "./MisComisionesResultadoCard.vue";
import { computed, ref } from "vue";
import { useOfertasEnergiaRetribucionListInfiniteScroll } from "@/composables/useOfertasEnergiaRetribucionListInfiniteScroll";
import { useApi } from "@/.shared/.api/integration.js";
import { saveAs } from "file-saver";
import { useIntl } from "@/.shared/services/i18n/i18nservice.js";

const PAGE_SIZE = 4;

const props = defineProps<{
  filtrosAplicados: OfertasEnergiaRetribucionesListFiltrosModel | null;
}>();

const impuestosActivo = ref(false);
const api = useApi();
const intl = useIntl();

const descargaCondicionesHabilitada = computed(() => !!props.filtrosAplicados?.Comercializadora);

const {
  items,
  totalItems,
  loading,
  loadError,
  hasMore,
  reachBottomEnabled,
  onReachBottom,
} = useOfertasEnergiaRetribucionListInfiniteScroll(() => props.filtrosAplicados, { pageSize: PAGE_SIZE });

async function descargarCondicionesComisionPDF(): Promise<void> {
  if (!props.filtrosAplicados?.Comercializadora) {
    return;
  }

  try {
    const file = await api.callStandardApi(
      "exportComisionesComercializadoraMiEmpresaPdf",
      {
        showLoading: true,
        isFile: true
      },
      props.filtrosAplicados.Comercializadora
    );
    saveAs(file);
  }
  catch(error: any){
    toastError(intl.i18n.t("mis_datos.comisiones.resultados.error_descarga_pdf"));
  }
}

async function descargarCondicionesComisionExcel(): Promise<void> {
  const filtros = props.filtrosAplicados;
  if (!filtros?.Comercializadora) {
    return;
  }

  try {
    const file = await api.callStandardApi(
      "exportComisionesComercializadoraMiEmpresaExcel",
      { showLoading: true, isFile: true },
      filtros.Comercializadora
    );

    saveAs(file);
  } catch {
    toastError("No se ha podido descargar el fichero de condiciones económicas");
  }
}

</script>

<style lang="scss" scoped>

.mis-comisiones-resultado {
  min-height: 0;
  overflow: hidden;
  height: 100%;
  display: grid;
  grid-template-rows: auto 1fr;
  grid-row-gap: var(--spacing-sm);

  /* Sin spinner ni texto del pocket “reach bottom” de DevExtreme; la carga se indica en la plantilla. */
  :deep(.dx-scrollview-scrollbottom-indicator),
  :deep(.dx-scrollview-scrollbottom-text),
  :deep(.dx-scrollview-scrollbottom-image) {
    display: none !important;
  }

  :deep(.dx-scrollview-scrollbottom) {
    padding-top: 0;
    padding-bottom: 0;
    min-height: 0;
  }
}

.mis-comisiones-resultados-header {
  min-height: 40px;
  padding: 0 var(--spacing-md);


  &:deep(.dx-radiogroup .dx-radiobutton) {
    margin-top: 0;
    margin-bottom: 0;
  }
}
</style>
